import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useStore } from "react-redux";
import { Button, ModalHeader, ModalBody, ModalFooter, Progress } from "reactstrap";

import { parseFileSize, getFileSizeString } from "utils/FileSizeUtils";
import useTypedSelector from "utils/useTypedSelector";
import ImagePreview from "../FileUpload/ImagePreview";
import { fileUploadDuck } from "../FileUpload/FileUploadDuck";
import ImageScaler from "./ImageScaler";

import { imagePickerDuck } from "./ImagePickerDuck";
import { useAppDispatch } from "src/utils/ReduxHooks";
import { S3Uploader } from "../FileUpload/S3Uploader";
import { S3TokenAccessor } from "../FileUpload/S3TokenAccessor";

let uploadAction = null;


/**
 *
 * @param {{
 *  files: File[] | FileList,
 *  onSubmit: (id:string)=>void,
 *  onCancel: ()=>void,
 *  accept: string,
 *  languageService: import('features/language/languageService').LanguageService,
 *  maxsize: number| string,
 *  imagePreviewOptions?: {name: string, maxwidth: number, maxheight: number }[],
 *  }} props
 */
const ImageDrop = ({ files, onSubmit, accept, languageService, maxsize, imagePreviewOptions, onCancel }) => {

    const t = languageService;
    const dispatch = useAppDispatch();
    const store = useStore();

    const maxSizeInBytes = parseFileSize(maxsize);
    const maxSizeString = getFileSizeString(maxSizeInBytes);

    const allowedTypes = accept.replace(/,/g, ", ");

    const { isUploading, percentUploaded } = useTypedSelector(state => state.fileUpload, shallowEqual);
    const user = useTypedSelector(state => state.login.user);


    const setUploadAction = useCallback((action) => {
        uploadAction = action;
    }, []);

    useEffect(() => {
        dispatch(fileUploadDuck.setFile(null));
        // eslint-disable-next-line
    }, []);

    const doSaveImage = () => {
        uploadAction();

    }

    const tempUploadReady = async (file) => {

        const width = store.getState()["imagepicker"].imagePreviewWidth;

        const data = {
            FileName: file.name,
            UploadedFilename: file.key,
            MaxWidth: width
        }

        try {

            const serveData = await imagePickerDuck.addImageToSystem(data);
            if (serveData) {
                onSubmit(serveData.ImageId);
            }

        }
        finally {
            dispatch(fileUploadDuck.setImagePreviews(null));
            uploadAction = null;
            dispatch(imagePickerDuck.setIsUploading(false));
        }
    }



    useEffect(() => {
        /**
         * @param {File} fi 
         */
        const doUpload = (fi) => {

            dispatch(fileUploadDuck.startUpload({}));

            const uploader = S3Uploader.startUpload(
                S3TokenAccessor,
                user.Id,
                fi,
                uploadData => {
                    const percent = 100 * uploadData.loaded / uploadData.total;
                    dispatch(fileUploadDuck.setPercentage(percent));
                }
            );

            uploader.promise.then((file) => {
                dispatch(fileUploadDuck.setUploading(false));
                let transformedFileData = {};
                Object.keys(file)
                    .forEach(k => transformedFileData[k.toLowerCase()] = file[k]);

                transformedFileData.name = fi.name;
                transformedFileData.size = fi.size;
                transformedFileData.type = fi.type;
                transformedFileData.created = fi.lastModified;
                tempUploadReady(transformedFileData);
            }).catch(error => {
                console.error("S3 upload failed:", error);
                dispatch(fileUploadDuck.setUploading(false));
                dispatch(imagePickerDuck.setIsUploading(false));
                dispatch(fileUploadDuck.setError(t.getText("file.upload.error")));
                uploadAction = null;
            });
        }


        if (!files || files.length === 0) {
            onCancel();
            return;
        }

        const fi = files[0];

        if (fi.size > maxSizeInBytes) {
            dispatch(fileUploadDuck.setError(t.getText("file.too.big", fi.name, getFileSizeString(fi.size), maxSizeString)));
            return;
        }

        const splits = fi.name.split(".");
        if (splits && splits.length > 1) {
            var ext = splits[splits.length - 1].toLowerCase();
            if (accept.indexOf(ext) < 0) {
                return;
            }
        }

        if (imagePreviewOptions != null && imagePreviewOptions.length > 0) {
            ImagePreview(fi, imagePreviewOptions)
                .then(data => {
                    dispatch(fileUploadDuck.setImagePreviews(data))
                    const width = data.full?.originalWidth;
                    if (width) {
                        dispatch(imagePickerDuck.setImagePreviewWidth(width));
                    }
                    setUploadAction(() => doUpload(fi));
                });

        }
        // eslint-disable-next-line
    }, [accept, setUploadAction, files, user, dispatch, maxSizeInBytes, t, allowedTypes, maxSizeString, imagePreviewOptions]);


    return (<>

        <ModalHeader className="bg-light" toggle={() => onCancel()} >{t.getText("image")}</ModalHeader>
        <ModalBody>

            <div className={"mt-3 " + (isUploading ? "" : "d-none")}>
                <Progress
                    color="success"
                    value={percentUploaded}
                />{" "}
                &nbsp;
            </div>

            <ImageScaler onSaveImage={doSaveImage} />

        </ModalBody>
        <ModalFooter>
            <Button className="btn btn-inverse" onClick={() => onCancel()}>{t.getText("close")}</Button>
        </ModalFooter>
    </>



    );
}

export default ImageDrop;